import { useCallback, useMemo, useState } from 'react';
import { Button } from 'rizzui';
import { IoArrowBackOutline } from 'react-icons/io5';
import { useNavigate, useParams } from 'react-router-dom';
import AttivaCard from '../../../components/card/AttivaCard';
import { Loader } from '../../../components/loader/SpinnerLogo';
import { usePrivilege } from '../../../components/priviledge/PriviledgeProvider';
import useFetch from '../../../hooks/useFetch';
import PageHeader from '../../../layout/shared/page-header';
import { menuRoutes } from '../../../router/menu';
import { DepartmentService } from '../../../services/department/departmentService';
import { NetworkService } from '../../../services/network/networkService';
import { InfoCard, InfoField, InfoGroup, formatInfoDate, formatInfoValue } from '../../contracts/profile/views/info/components/InfoDisplay';
import NetworkForm from '../NetworkForm';

const breadCrumb = [
    { name: 'Administración' },
    { name: 'Redes comerciales y departamentos', href: menuRoutes.networks.path },
    { name: 'Detalle de red' },
];

type ViewMode = 'summary' | 'edit';

const tabStyles = {
    container: 'max-w-[99vw] sticky z-20 -mx-4 -mt-4 border-b border-muted bg-white px-4 py-0 font-medium text-gray-500 md:-mx-5 md:px-5 lg:-mx-8 lg:mt-0 lg:px-8 xl:-mx-6 xl:px-6 3xl:-mx-[33px] 3xl:px-[33px] 4xl:-mx-10 4xl:px-10 top-[62px] md:top-[71px]',
    list: '-mb-7 flex w-full gap-3 overflow-x-auto scroll-smooth pb-7 md:gap-5 lg:gap-8',
    tab: 'group relative cursor-pointer whitespace-nowrap py-2.5 font-medium text-gray-500',
    active: 'text-primary before:visible before:w-full before:opacity-100',
    inactive: 'before:invisible before:w-0 before:opacity-0 hover:text-gray-900',
};

const NetworkDetailPage = () => {
    const navigate = useNavigate();
    const { userCan } = usePrivilege();
    const { id } = useParams();
    const [viewMode, setViewMode] = useState<ViewMode>('summary');

    const [network, loadingNetwork, networkError, refetchNetwork] = useFetch(useCallback(async () => {
        if (!id) return { success: false, data: null, message: 'missing-id' };
        const response = await (new NetworkService()).getNetworkById(id);
        return response.getResponseData();
    }, [id]));

    const [departmentsResponse, loadingDepartments, departmentsError, refetchDepartments] = useFetch(useCallback(async () => {
        if (!id) return { success: false, data: null, message: 'missing-id' };
        const response = await (new DepartmentService()).getDepartmentsByCommercialNetwork(id);
        return response.getResponseData();
    }, [id]));

    const childNetworks = useMemo(() => {
        const entries = network?.childrenCommercialNetworks ?? network?.childrenCommercialNetwork ?? [];
        return (entries || [])
            .map((item: any) => item?.commercialNetwork ?? item?.childCommercialNetwork ?? item)
            .filter((item: any) => item?.id && item?.name)
            .sort((a: any, b: any) => a.name.localeCompare(b.name));
    }, [network]);

    const departments = useMemo(() => {
        const fromEndpoint = (departmentsResponse?.data ?? departmentsResponse ?? [])
            .map((item: any) => item?.department ?? item)
            .filter((item: any) => item?.id && item?.name);
        const fromNetwork = (network?.departments ?? network?.department ?? [])
            .map((item: any) => item?.department ?? item)
            .filter((item: any) => item?.id && item?.name);
        const map = new Map<string, any>();
        [...fromEndpoint, ...fromNetwork].forEach((item: any) => map.set(item.id, item));
        return Array.from(map.values()).sort((a: any, b: any) => a.name.localeCompare(b.name));
    }, [departmentsResponse, network]);

    const linkedCorporations = useMemo(() => {
        return (network?.corporations ?? [])
            .map((item: any) => item?.corporation ?? item)
            .filter((item: any) => item?.id && item?.name)
            .sort((a: any, b: any) => a.name.localeCompare(b.name));
    }, [network]);

    const loading = loadingNetwork || loadingDepartments;
    const hasError = networkError || departmentsError;
    const canEditNetwork = userCan('edit_commercial_network', 'commercial_network');

    return (
        <>
            <PageHeader title="Detalle de red comercial" breadcrumb={breadCrumb}>
                <div className="flex items-center gap-2">
                    <Button variant="flat" size="sm" onClick={() => navigate(-1)}>
                        <IoArrowBackOutline className="me-2 h-4 w-4" />
                        Volver atrás
                    </Button>
                </div>
            </PageHeader>

            <div className={tabStyles.container}>
                <div className="relative flex items-center overflow-hidden">
                    <div className="flex h-[52px] items-start overflow-hidden w-full">
                        <div className={tabStyles.list}>
                            <button
                                type="button"
                                className={`${tabStyles.tab} before:absolute before:bottom-0 before:left-0 before:z-[1] before:h-0.5 before:bg-gray-1000 before:transition-all ${viewMode === 'summary' ? tabStyles.active : tabStyles.inactive}`}
                                onClick={() => setViewMode('summary')}
                            >
                                <span className="inline-flex rounded-md px-2.5 py-1.5 transition-all duration-200 group-hover:bg-gray-100/70">Información</span>
                            </button>
                            {canEditNetwork && (
                                <button
                                    type="button"
                                    className={`${tabStyles.tab} before:absolute before:bottom-0 before:left-0 before:z-[1] before:h-0.5 before:bg-gray-1000 before:transition-all ${viewMode === 'edit' ? tabStyles.active : tabStyles.inactive}`}
                                    onClick={() => setViewMode('edit')}
                                >
                                    <span className="inline-flex rounded-md px-2.5 py-1.5 transition-all duration-200 group-hover:bg-gray-100/70">Editar</span>
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {loading && <Loader height={'70'} />}

            {!loading && (hasError || !network) && (
                <AttivaCard title="" className="text-sm text-gray-600">
                    No se ha podido cargar la red comercial solicitada.
                </AttivaCard>
            )}

            {!loading && network && viewMode === 'summary' && (
                <div className="mt-3 flex flex-col gap-5 w-full transition-all duration-300 ease-in-out md:relative pe-2">
                    <InfoCard title="Datos de la Red Comercial" subtitle="Información operativa y organizativa de la red y sus relaciones.">
                        <InfoGroup title="Información General" description="Identificación y trazabilidad de la red comercial.">
                            <InfoField label="Nombre" value={formatInfoValue(network.name)} className="md:col-span-4" />
                            <InfoField label="Código" value={formatInfoValue(network.code)} className="md:col-span-4" />
                            <InfoField label="IP" value={formatInfoValue(network.ipAddress)} className="md:col-span-4" />
                            <InfoField label="Creación" value={formatInfoDate(network.createdAt)} className="md:col-span-4" />
                            <InfoField label="Actualización" value={formatInfoDate(network.updatedAt)} className="md:col-span-4" />
                            <InfoField label="Red padre" value={formatInfoValue(network.parentCommercialNetwork?.name)} className="md:col-span-4" />
                        </InfoGroup>

                        <InfoGroup title="Contacto y Métricas" description="Datos de contacto y métricas de estructura de la red.">
                            <InfoField label="Email" value={formatInfoValue((network as any).email)} className="md:col-span-4" />
                            <InfoField label="Teléfono" value={formatInfoValue((network as any).telephone)} className="md:col-span-4" />
                            <InfoField label="Nº departamentos" value={formatInfoValue(departments.length)} className="md:col-span-4" />
                            <InfoField label="Nº redes hijas" value={formatInfoValue(childNetworks.length)} className="md:col-span-4" />
                            <InfoField label="Nº empresas vinculadas" value={formatInfoValue(linkedCorporations.length)} className="md:col-span-4" />
                        </InfoGroup>

                        <InfoGroup title="Departamentos Relacionados" description="Departamentos enlazados a esta red comercial.">
                            <InfoField
                                label="Listado de departamentos"
                                className="md:col-span-12"
                                value={departments.length > 0 ? (
                                    <div className="flex flex-col gap-1">
                                        {departments.map((department: any) => (
                                            <button
                                                key={department.id}
                                                type="button"
                                                className="text-left text-primary hover:underline"
                                                onClick={() => navigate(`${menuRoutes.departments.path}/detail/${department.id}`)}
                                            >
                                                {department.name}
                                            </button>
                                        ))}
                                    </div>
                                ) : 'N/A'}
                            />
                        </InfoGroup>

                        <InfoGroup title="Otras Entidades Relacionadas" description="Navegación cruzada con redes hijas y empresas vinculadas.">
                            <InfoField
                                label="Redes hijas"
                                className="md:col-span-6"
                                value={childNetworks.length > 0 ? (
                                    <div className="flex flex-col gap-1">
                                        {childNetworks.map((child: any) => (
                                            <button
                                                key={child.id}
                                                type="button"
                                                className="text-left text-primary hover:underline"
                                                onClick={() => navigate(`${menuRoutes.networks.path}/detail/${child.id}`)}
                                            >
                                                {child.name}
                                            </button>
                                        ))}
                                    </div>
                                ) : 'N/A'}
                            />
                            <InfoField
                                label="Empresas vinculadas"
                                className="md:col-span-6"
                                value={linkedCorporations.length > 0 ? (
                                    <div className="flex flex-col gap-1">
                                        {linkedCorporations.map((corporation: any) => (
                                            <button
                                                key={corporation.id}
                                                type="button"
                                                className="text-left text-primary hover:underline"
                                                onClick={() => navigate(`${menuRoutes.corporations.path}/detail/${corporation.id}`)}
                                            >
                                                {corporation.name}
                                            </button>
                                        ))}
                                    </div>
                                ) : 'N/A'}
                            />
                        </InfoGroup>
                    </InfoCard>
                </div>
            )}

            {!loading && network && canEditNetwork && viewMode === 'edit' && (
                <div className="mt-3 flex flex-col gap-5 w-full transition-all duration-300 ease-in-out md:relative pe-2">
                    <AttivaCard title="Edición de Red Comercial" className="border-transparent lg:p-0">
                        <NetworkForm
                            id={id}
                            setNodeId={() => { }}
                            isOpen
                            setIsOpen={(value) => {
                                if (!value) navigate(-1);
                            }}
                            refetch={() => {
                                refetchNetwork();
                                refetchDepartments();
                            }}
                        />
                    </AttivaCard>
                </div>
            )}
        </>
    );
};

export default NetworkDetailPage;
